import React from 'react';
import { UserIcon, BuildingOfficeIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

interface RoleOption {
  role: string;
  firstName: string;
  lastName: string;
  companyName?: string;
}

interface RoleSelectionModalProps {
  isOpen: boolean;
  email: string;
  availableRoles: RoleOption[];
  onSelectRole: (role: string) => void;
  onCancel: () => void;
}

const RoleSelectionModal: React.FC<RoleSelectionModalProps> = ({
  isOpen,
  email,
  availableRoles,
  onSelectRole,
  onCancel,
}) => {
  if (!isOpen) return null;

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'candidate':
        return <UserIcon className="w-8 h-8" />;
      case 'employer':
        return <BuildingOfficeIcon className="w-8 h-8" />;
      case 'admin':
        return <ShieldCheckIcon className="w-8 h-8" />;
      default:
        return <UserIcon className="w-8 h-8" />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'candidate':
        return 'bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300';
      case 'employer':
        return 'bg-green-50 hover:bg-green-100 border-green-200 text-green-800 dark:bg-green-900/20 dark:hover:bg-green-900/30 dark:border-green-700 dark:text-green-300';
      case 'admin':
        return 'bg-admin-50 hover:bg-admin-100 border-admin-200 text-admin-800 dark:bg-admin-900/20 dark:hover:bg-admin-900/30 dark:border-admin-700 dark:text-admin-300';
      default:
        return 'bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-800 dark:bg-gray-900/20 dark:hover:bg-gray-900/30 dark:border-gray-700 dark:text-gray-300';
    }
  };

  const getRoleTitle = (role: string) => {
    switch (role) {
      case 'candidate':
        return 'Job Seeker';
      case 'employer':
        return 'Employer';
      case 'admin':
        return 'Administrator';
      default:
        return role;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Select Your Role
          </h2>
          <p className="text-gray-600 dark:text-gray-300">
            Multiple accounts found for <strong>{email}</strong>
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Choose which account you'd like to access
          </p>
        </div>

        <div className="space-y-3">
          {availableRoles.map((roleOption, index) => (
            <button
              key={index}
              onClick={() => onSelectRole(roleOption.role)}
              className={`w-full p-4 rounded-lg border-2 transition-colors duration-200 ${getRoleColor(roleOption.role)}`}
            >
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  {getRoleIcon(roleOption.role)}
                </div>
                <div className="flex-1 text-left">
                  <div className="font-semibold">
                    {getRoleTitle(roleOption.role)}
                  </div>
                  <div className="text-sm opacity-75">
                    {roleOption.firstName} {roleOption.lastName}
                    {roleOption.companyName && (
                      <span className="block">{roleOption.companyName}</span>
                    )}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-6 flex justify-center">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoleSelectionModal;